import { useOrganizationList } from "@/api/services/admin/organization";
import { TGetOrganizationTypeDto } from "@/api/services/admin/organization/dtos/get-organization-list.dto";
import { Table, TableSorting } from "@/modules/irohana-components/elements/Table";
import { CountTable } from "@/modules/irohana-components/elements/CountTable";
import { PaginationNew } from "@/modules/irohana-components/elements/PaginationNew";
import { EMPTY_STR } from "@/constants/string";
import { formatDate } from "@/features/organization/utils/format";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { EOrganizationContractStatus, TOrganizationContractStatus } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type TOrganizationTableProps = {
  contractStatus?: TOrganizationContractStatus;
};

type TDateColumnConfig = {
  header: string;
  accessorKey: keyof TGetOrganizationTypeDto;
  sortName: string;
};

// The date column shown alongside each contract-status tab differs per tab.
const DATE_COLUMN_BY_STATUS: Record<TOrganizationContractStatus, TDateColumnConfig> = {
  [EOrganizationContractStatus.DURING_CONTRACT]: {
    header: "契約開始日",
    accessorKey: "contractStartDate",
    sortName: "organization.contractStartDate",
  },
  [EOrganizationContractStatus.CANCELED_CONTRACT]: {
    header: "解約日",
    accessorKey: "contractEndDate",
    sortName: "organization.contractEndDate",
  },
  [EOrganizationContractStatus.TRIAL]: {
    header: "トライアル開始日",
    accessorKey: "trialStartDate",
    sortName: "organization.trialStartDate",
  },
  [EOrganizationContractStatus.TRIAL_CANCELLED]: {
    header: "トライアル終了日",
    accessorKey: "trialEndDate",
    sortName: "organization.trialEndDate",
  },
};

export const OrganizationTable: FC<TOrganizationTableProps> = ({ contractStatus }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useOrganizationList(contractStatus);
  const [heightTable] = useState<string>("calc(100vh - 376px)");

  const columns = useMemo<MRT_ColumnDef<TGetOrganizationTypeDto>[]>(() => {
    const dateColumn = DATE_COLUMN_BY_STATUS[contractStatus ?? EOrganizationContractStatus.DURING_CONTRACT];

    return [
      {
        header: "組織名",
        id: "name",
        accessorKey: "name",
        Header: <TableSorting name="organization.name">組織名</TableSorting>,
        Cell: ({ row }) => truncateWithEllipsis(row.original.name, 40),
        size: 320,
      },
      {
        header: "契約プラン",
        id: "contractPlan",
        Header: <TableSorting name="organization.contractPlan">契約プラン</TableSorting>,
        Cell: ({ row }) => truncateWithEllipsis(row.original.contractPlan ?? EMPTY_STR.TEXT, 30),
        size: 200,
      },
      {
        header: dateColumn.header,
        id: dateColumn.accessorKey,
        Header: <TableSorting name={dateColumn.sortName}>{dateColumn.header}</TableSorting>,
        Cell: ({ row }) => formatDate(row.original[dateColumn.accessorKey] as string | null | undefined),
        size: 160,
      },
    ];
  }, [contractStatus]);

  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.organizationDetail, { id: row.id })),
    renderEmptyRowsFallback: () => <></>,
  });

  return (
    <>
      <Box mb={1}>
        <CountTable total={data?.pagination.total} page={data?.pagination.page} perPage={data?.pagination.perPage} />
      </Box>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <Box mt={1}>
        <PaginationNew totalPage={data?.pagination.totalPage} />
      </Box>
    </>
  );
};
